import { Luggage } from "lucide-react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tripsApi } from "../../../api";
import InfoCard from "../../../components/InfoCard";
import PillButton from "../../../components/PillButton";
import RouteConnector from "../../../components/RouteConnector";
import { APIContext } from "../../../connector";
import { useToast } from "../../../hooks/useToast";
import backArrow from "../../../static/images/backArrow.svg";
import profileIcon from "../../../static/images/profile-icon.svg";
import classes from "./TripHistory.module.scss";

const infoCardTitle = "Completed Trip";

const TripHistory = () => {
  const navigate = useNavigate();
  const { userData } = useContext(APIContext);
  const { openToast } = useToast();

  const [history, setHistory] = useState(null);
  const isCurrentlyDriver = userData?.user_type === "DRIVER";

  const fetchHistory = useCallback(async () => {
    try {
      const data = await tripsApi.getTripHistory();
      setHistory(data);
    } catch (e) {
      console.error("Error fetching history:", e);
      openToast("An error occurred while fetching history", 5000, "danger");
      setHistory([]);
    }
  }, [openToast]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.backArrow} onClick={() => navigate(-1)}>
        <img
          className={classes.backArrowIcon}
          src={backArrow || "/placeholder.svg"}
          alt="back arrow"
        />
      </div>

      <h1 className={classes.title}>Ride History</h1>
      <p className={classes.subtitle}>Trips from the past 24 hours</p>

      {(!history || history.length === 0) && (
        <div className={classes.emptyOrderList}>
          <p>{!history ? "Loading history..." : "No trips found in the last 24h!"}</p>
        </div>
      )}

      {history && history.length > 0 && (
        <div className={classes.orderList}>
          {history.map((row) => {
            const participantLabel = isCurrentlyDriver ? "User" : "Driver";
            const participantDetails = isCurrentlyDriver ? row.user_details : row.driver_details;
            const profileUrl = `/profile/${participantDetails?.user_id}`;
            const hasParticipant = !!participantDetails?.user_id;

            return (
              <InfoCard
                key={row.trip_id}
                className={classes.infoCard}
                title={infoCardTitle}
                time={formatTime(row.updated_time || row.created_time)}
                variant="orders"
              >
                <div className={classes.infoCardContainer}>
                  <div className={classes.mainContent}>
                    <div className={classes.locationsWrapper}>
                      <div className={classes.locationsImage}>
                        <RouteConnector />
                      </div>
                      <div className={classes.locationsContent}>
                        {/* Top Row */}
                        <div className={classes.row}>
                          <p className={classes.locationAddress}>{row.from_location}</p>
                          <p className={classes.pax}>
                            {row.have_luggage && <Luggage size={20} />} PAX {row.pax}
                          </p>
                        </div>

                        <hr className={classes.separator} />

                        {/* Bottom Row */}
                        <div className={classes.row}>
                          <p className={classes.locationAddress}>{row.to_location}</p>
                          <div className={classes.priceContainer}>
                            <p className={classes.price}>RM {Number(row.final_price).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Name Section */}
                    <div className={classes.nameSection}>
                       <p className={classes.participantName}>
                        {participantDetails?.name || "Unknown"}
                      </p>
                    </div>

                    {/* Profile Button Section */}
                    {hasParticipant && (
                      <>
                        <hr className={classes.profileSeparator} />
                        <div className={classes.actionButtonsContainer}>
                          <PillButton
                            className={classes.actionButton}
                            onClick={() =>
                              navigate(profileUrl, {
                                state: { profileData: participantDetails },
                              })
                            }
                          >
                            <img
                              src={profileIcon}
                              alt="profile"
                              className={classes.btnIcon}
                            />
                            <span>{participantLabel} Profile</span>
                          </PillButton>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </InfoCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TripHistory;
